import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableExpandedRows } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import EichrechtDecisionDialog from "../Prospects/components/EichrechtDecisionDialog";
import {
  EichrechtDecisionEntry,
  EichrechtDecisionView,
  EichrechtOverview,
  EichrechtProspectRef,
  listEichrechtDecisions,
} from "../../../services/onboarding/eichrecht";
import { EichrechtVerdict } from "../../../types/onboarding";
import { actorLabel } from "../../../utils/actors";

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const VERDICT: Record<EichrechtVerdict, { label: string; severity: "success" | "danger" }> = {
  CONFORM: { label: "Eichrechtskonform", severity: "success" },
  NOT_CONFORM: { label: "Not eichrechtskonform", severity: "danger" },
};

const decidedText = (entry: EichrechtDecisionEntry) => `${actorLabel(entry.decided_by) || entry.decided_by}, ${formatDate(entry.decided_at)}`;

const Customers: React.FC<{ prospects: EichrechtProspectRef[] }> = ({ prospects }) =>
  prospects.length ? (
    <span>
      {prospects.map((p, i) => (
        <React.Fragment key={p.id}>
          {i > 0 && ", "}
          <Link to={`/onboarding/prospects/${p.id}`}>{p.name}</Link>
        </React.Fragment>
      ))}
    </span>
  ) : (
    <span className="text-500">—</span>
  );

const History: React.FC<{ decision: EichrechtDecisionView }> = ({ decision }) =>
  decision.history.length === 0 ? (
    <p className="m-0 text-600">No earlier decision.</p>
  ) : (
    <ul className="m-0 pl-3" aria-label={`Earlier decisions for ${decision.manufacturer} – ${decision.model}`}>
      {decision.history.map((entry, i) => (
        <li key={`${entry.decided_at}-${i}`} className="mb-1">
          {VERDICT[entry.verdict].label}: {entry.note}
          <span className="text-600"> — {decidedText(entry)}</span>
        </li>
      ))}
    </ul>
  );

type Deciding = { manufacturer: string; model: string };

/**
 * Support's Eichrecht decisions for unlisted wallboxes, one per model (CWA-230,
 * SOPS-195), and the models still waiting for one. Each decision is reused for
 * every later customer with the model; a not-conform one blocks nobody but its
 * customers show a warning to support.
 */
const EichrechtDecisionsPanel: React.FC = () => {
  const [overview, setOverview] = useState<EichrechtOverview | null>(null);
  const [failed, setFailed] = useState(false);
  const [manufacturer, setManufacturer] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<DataTableExpandedRows | undefined>(undefined);
  const [deciding, setDeciding] = useState<Deciding | null>(null);

  const load = useCallback(() => {
    listEichrechtDecisions()
      .then((data) => {
        setOverview(data);
        setFailed(false);
      })
      .catch(() => {
        setOverview({ decisions: [], pending: [] });
        setFailed(true);
      });
  }, []);

  useEffect(load, [load]);

  const manufacturers = useMemo(
    () =>
      Array.from(new Set([...(overview?.decisions ?? []), ...(overview?.pending ?? [])].map((m) => m.manufacturer))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [overview]
  );
  const matches = (m: { manufacturer: string }) => !manufacturer || m.manufacturer === manufacturer;
  const pending = (overview?.pending ?? []).filter(matches);
  const decisions = (overview?.decisions ?? []).filter(matches);

  return (
    <div>
      <p className="mt-0 text-600">
        Unlisted wallboxes support has checked for Eichrecht conformity, and the ones still waiting. A decision applies to
        every customer with the same wallbox model. Not eichrechtskonform blocks nobody; its customers show a warning.
      </p>
      {failed && <Message severity="error" className="w-full mb-3" text="The Eichrecht decisions could not be loaded." />}
      {deciding && (
        <EichrechtDecisionDialog
          manufacturer={deciding.manufacturer}
          model={deciding.model}
          visible
          onHide={() => setDeciding(null)}
          onDecided={load}
        />
      )}
      <div className="mb-3">
        <label htmlFor="eichrecht-manufacturer" className="mr-2">
          Manufacturer
        </label>
        <Dropdown
          inputId="eichrecht-manufacturer"
          value={manufacturer}
          options={manufacturers.map((name) => ({ label: name, value: name }))}
          showClear
          placeholder="All manufacturers"
          onChange={(e) => setManufacturer(e.value ?? null)}
        />
      </div>

      <h3 className="mb-2">Waiting for a check</h3>
      {overview && pending.length === 0 && <p className="text-600">No wallbox is waiting for a check.</p>}
      {pending.map((m) => (
        <div key={m.model_key} className="surface-50 border-round p-3 mb-2 flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <div className="font-semibold">{`${m.manufacturer} – ${m.model}`}</div>
            <div className="text-sm">
              Holds back: <Customers prospects={m.prospects} />
            </div>
          </div>
          <Button
            label="Decide"
            icon="pi pi-check-square"
            aria-label={`Decide ${m.manufacturer} – ${m.model}`}
            onClick={() => setDeciding({ manufacturer: m.manufacturer, model: m.model })}
          />
        </div>
      ))}

      <h3 className="mb-2">Decisions</h3>
      <DataTable
        value={decisions}
        dataKey="model_key"
        loading={overview === null}
        emptyMessage="No Eichrecht decision has been recorded."
        expandedRows={expanded}
        onRowToggle={(e) => setExpanded(e.data as DataTableExpandedRows)}
        rowExpansionTemplate={(d: EichrechtDecisionView) => <History decision={d} />}
        aria-label="Eichrecht decisions"
      >
        <Column expander style={{ width: "3rem" }} />
        <Column header="Wallbox" body={(d: EichrechtDecisionView) => `${d.manufacturer} – ${d.model}`} />
        <Column
          header="Decision"
          body={(d: EichrechtDecisionView) => <Tag severity={VERDICT[d.verdict].severity} value={VERDICT[d.verdict].label} />}
        />
        <Column header="Note" body={(d: EichrechtDecisionView) => d.note} />
        <Column header="Decided by" body={(d: EichrechtDecisionView) => decidedText(d)} />
        <Column header="Customers" body={(d: EichrechtDecisionView) => <Customers prospects={d.prospects} />} />
        <Column
          body={(d: EichrechtDecisionView) => (
            <Button
              label="Change"
              size="small"
              text
              aria-label={`Change the decision for ${d.manufacturer} – ${d.model}`}
              onClick={() => setDeciding({ manufacturer: d.manufacturer, model: d.model })}
            />
          )}
        />
      </DataTable>
    </div>
  );
};

export default EichrechtDecisionsPanel;
